// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {fetchEffectData, useDeepEffect} from 'Utils/react-utils';
import LifetimeReportChart from 'Ui/features/onequip/reports/lifetime-report/lifetime-report-chart';
import LifetimeReportDataTable from 'Ui/features/onequip/reports/lifetime-report/lifetime-report-data-table';
import NoDataMessage from 'Ui/components/common/message/no-data-message';
import {getTableData} from 'Ui/features/onequip/reports/lifetime-report/lifetime-report-utils';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {VIEW_EQUIPMENT_DETAILS} from 'Common/constants/business-activities';
import {IconWdt} from '@deere/icons';
import {exportLifetimeUptimeReport} from 'Services/excel-service';
import LaborReportExportDialog from 'OnLabor/report/labor-report-export-dialog';
import {getLifetimeExportContent} from 'Utils/labor-report-export-utils';
import useReportExportDialog from 'Ui/react-hooks/use-report-export-dialog';

function EquipmentLifetimeReport(props) {
    const {
        membership,
        isExportPreviewEnabled,
        primarySelector,
        secondarySelector,
        setLoading,
        translations,
        loading,
        addToast
    } = props;

    const [lifetimeData, setLifetimeData] = React.useState({
        chartData: {},
        tableData: []
    });
    const exportContent = React.useMemo(() => getLifetimeExportContent(lifetimeData.tableData, primarySelector, translations), [lifetimeData.tableData, primarySelector, translations]);
    const downloadReport = React.useCallback(() => exportLifetimeUptimeReport(
        lifetimeData.tableData,
        primarySelector,
        translations,
        addToast
    ), [lifetimeData.tableData, primarySelector, translations, addToast]);
    const {
        handleClose,
        handleDownload,
        handleOpen,
        isOpen
    } = useReportExportDialog(downloadReport, isExportPreviewEnabled);

    useDeepEffect(() => fetchEffectData(async (isMounted) => {
        if (!secondarySelector) {
            setLoading((prev) => ({
                ...prev,
                dateSelect: false,
                primarySelector: false,
                secondarySelector: false
            }));
            return;
        }
        let tableData = [],
            chartData = {};

        try {
            [tableData, chartData] = await getTableData(
                primarySelector,
                secondarySelector,
                membership.fleetId,
                {
                    currencyPreference: membership.currencyPreference
                },
                translations
            );
        } finally {
            if (isMounted()) {
                setLifetimeData({
                    chartData,
                    tableData
                });
                setLoading((prevLoading) => ({
                    ...prevLoading,
                    dateSelect: false,
                    primarySelector: false,
                    secondarySelector: false
                }));
            }
        }
    }), [membership.currencyPreference, membership.fleetId, primarySelector, secondarySelector, translations]);

    useNavBarActions([{
        disabled: loading.equipment,
        Icon: <IconWdt/>,
        onClick: handleOpen,
        myJdPermissions: VIEW_EQUIPMENT_DETAILS,
        title: 'EXPORT',
        variant: 'primary'
    }]);

    return (
        <>
            <NoDataMessage
                hasData={lifetimeData.tableData.length > 0}
                noDataMessage={translations.ONLINK_NO_DATA_GRAPH}
            >
                <div>
                    <div className='chart-container'>
                        <LifetimeReportChart
                            chartData={lifetimeData.chartData}
                            currencyPreference={membership.currencyPreference}
                            primarySelector={primarySelector}
                            translations={translations}
                        />
                    </div>
                    <LifetimeReportDataTable
                        primarySelector={primarySelector}
                        secondarySelector={secondarySelector}
                        tableData={lifetimeData.tableData}
                        translations={translations}
                    />
                </div>
            </NoDataMessage>
            {
                isOpen &&
                <LaborReportExportDialog
                    dateRange={translations.LIFETIME}
                    onCancel={handleClose}
                    onDownload={handleDownload}
                    rows={[
                        exportContent.header,
                        ...exportContent.rows
                    ]}
                    translations={translations}
                />
            }
        </>
    );
}

EquipmentLifetimeReport.propTypes = {
    addToast: PropTypes.func,
    isExportPreviewEnabled: PropTypes.bool,
    loading: PropTypes.object,
    membership: PropTypes.membership,
    primarySelector: PropTypes.string,
    secondarySelector: PropTypes.string,
    setLoading: PropTypes.func,
    translations: PropTypes.translations
};

export default EquipmentLifetimeReport;
